<template>
  <div class="emotion-management">
    <div class="page-header">
      <h1>表情管理</h1>
      <div class="header-actions">
        <el-button type="primary" @click="showUploadDialog = true">
          <el-icon><Upload /></el-icon>
          上传表情包
        </el-button>
        <el-button
          v-if="isAdmin"
          type="primary"
          @click="goBuildPack"
        >
          <el-icon><Plus /></el-icon>
          制作表情包
        </el-button>
        <el-button @click="refreshEmotionList">
          <el-icon><Refresh /></el-icon>
          刷新
        </el-button>
      </div>
    </div>

    <!-- 表情包列表 -->
    <div class="emotion-list">
      <div class="table-scroll">
      <el-table
        v-loading="loading"
        :data="emotionPackages"
        stripe
        style="width: 100%"
        empty-text="暂无表情包"
      >
        <el-table-column prop="name" label="表情包名称" min-width="150" show-overflow-tooltip />
        <el-table-column label="表情包ID" min-width="200" show-overflow-tooltip>
          <template #default="{ row }">
            <div style="display:flex;align-items:center;gap:6px;">
              <code :title="row.id" style="font-size:12px;">{{ formatShortId(row.id) }}</code>
              <el-button size="small" text type="primary" @click="copyToClipboard(row.id)">复制</el-button>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="version" label="版本" width="80" />
        <el-table-column prop="category" label="分类" width="120" show-overflow-tooltip>
          <template #default="{ row }">
            <span>{{ row.category || "未分类" }}</span>
          </template>
        </el-table-column>
        <el-table-column label="适配硬件类型" min-width="140" show-overflow-tooltip>
          <template #default="{ row }">
            <span>{{ getBoardTypeNames(row.board_types) }}</span>
          </template>
        </el-table-column>
        <el-table-column v-if="isAdmin" label="购买数" width="120">
          <template #default="{ row }">
            <div>
              <div>真实: {{ row.real_purchase_count || 0 }}</div>
              <div style="color: #909399; font-size: 12px;">运营: {{ row.display_purchase_count || 0 }}</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="content" label="内容说明" min-width="150" show-overflow-tooltip />
        <el-table-column prop="hot" label="火热程度" width="90">
          <template #default="scope">
            <el-rate :model-value="scope.row.hot || 5" disabled :max="5" />
          </template>
        </el-table-column>
        <el-table-column v-if="isAdmin" label="审核状态" width="90">
          <template #default="{ row }">
            <el-tag :type="row.is_approved ? 'success' : 'warning'" size="small">
              {{ row.is_approved ? '已审核' : '待审核' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column v-if="isAdmin" label="上架状态" width="90">
          <template #default="{ row }">
            <el-tag :type="row.is_published ? 'success' : 'info'" size="small">
              {{ row.is_published ? '已上架' : '已下架' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column v-if="isAdmin" label="文件状态" width="80">
          <template #default="{ row }">
            <el-tag :type="row.file_exists ? 'success' : 'danger'">
              {{ row.file_exists ? '存在' : '缺失' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="原图" width="110">
          <template #default="{ row }">
            <div v-if="row.file_path" style="display:flex;align-items:center;gap:6px;">
              <img
                :src="getImageUrl(row.file_path)"
                alt="原图"
                style="width:40px;height:40px;object-fit:cover;border-radius:4px;border:1px solid #ebeef5;cursor:pointer;"
                @error="handleImageError"
                @click="openImagePreview(row.file_path)"
              />
            </div>
            <span v-else style="color:#909399;">-</span>
          </template>
        </el-table-column>
        <el-table-column label="上传来源" min-width="120" show-overflow-tooltip>
          <template #default="{ row }">
            <span>{{ getUploaderDisplay(row) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="创建时间" width="140">
          <template #default="{ row }">
            {{ formatDate(row.created_at) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="240" fixed="right">
          <template #default="{ row }">
            <div class="action-buttons">
              <el-button
                type="primary"
                size="small"
                @click="viewEmotionDetail(row.id)"
              >
                详情
              </el-button>
              <el-button
                type="success"
                size="small"
                :disabled="!row.file_exists"
                @click="downloadEmotion(row.id)"
              >
                下载
              </el-button>
              <el-button
                v-if="isAdmin || (row.owner_id && String(row.owner_id) === String(currentUser?.id))"
                type="warning"
                size="small"
                @click="editEmotion(row)"
              >
                编辑
              </el-button>
              <el-button
                v-if="isAdmin && !row.is_approved"
                type="success"
                size="small"
                @click="approveEmotion(row.id)"
              >
                审核
              </el-button>
              <el-button
                v-if="isAdmin && row.is_approved"
                :type="row.is_published ? 'warning' : 'success'"
                size="small"
                @click="togglePublishEmotion(row.id, !row.is_published)"
              >
                {{ row.is_published ? '下架' : '上架' }}
              </el-button>
              <el-button
                v-if="isAdmin && (!row.owner_id || String(row.owner_id) !== String(currentUser?.id))"
                type="primary"
                size="small"
                @click="onGrantAccess(row)"
              >
                授权
              </el-button>
              <el-button
                v-if="isAdmin || (row.owner_id && String(row.owner_id) === String(currentUser?.id))"
                type="danger"
                size="small"
                @click="deleteEmotion(row.id)"
              >
                删除
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
      </div>
    </div>

    <!-- 上传对话框 -->
    <el-dialog
      v-model="showUploadDialog"
      title="上传原图生成表情包"
      width="640px"
      :close-on-click-modal="false"
    >
      <el-form
        ref="uploadFormRef"
        :model="uploadForm"
        :rules="uploadRules"
        label-width="100px"
      >
        <el-form-item label="表情包名称" prop="name">
          <el-input v-model="uploadForm.name" placeholder="请输入表情包名称" />
        </el-form-item>
        <el-form-item label="版本号" prop="version">
          <el-input v-model="uploadForm.version" placeholder="如：1.0.0" />
        </el-form-item>
        <el-form-item label="分类" prop="category">
          <el-select
            v-model="uploadForm.category"
            style="width: 100%;"
            placeholder="请选择或输入分类名称"
            filterable
            allow-create
            default-first-option
          >
            <el-option v-for="item in categoryOptions" :key="item" :label="item" :value="item" />
          </el-select>
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input
            v-model="uploadForm.description"
            type="textarea"
            :rows="3"
            placeholder="请输入表情包描述"
          />
        </el-form-item>
        <el-form-item label="内容说明" prop="content">
          <el-input
            v-model="uploadForm.content"
            type="textarea"
            :rows="3"
            placeholder="请输入表情包内容说明"
          />
        </el-form-item>
        <el-form-item v-if="isAdmin" label="火热程度" prop="hot">
          <el-input-number
            v-model="uploadForm.hot"
            :min="1"
            :max="5"
            :step="1"
            placeholder="1-5，5最火爆"
            style="width: 100%;"
          />
          <div style="font-size: 12px; color: #909399; margin-top: 4px;">
            1表示最不火爆，5表示最火爆
          </div>
        </el-form-item>
        <el-form-item label="适配版型" prop="board_types">
          <el-checkbox-group v-model="uploadForm.board_types">
            <el-checkbox 
              v-for="board in boardTypes" 
              :key="board.id" 
              :value="board.id"
            >
              {{ board.name }}
            </el-checkbox>
          </el-checkbox-group>
        </el-form-item>
        <el-form-item label="原图文件" prop="file">
          <el-upload
            ref="uploadRef"
            :auto-upload="false"
            :on-change="handleFileChange"
            :before-upload="beforeUpload"
            :file-list="fileList"
            accept="image/*"
            drag
          >
            <el-icon class="el-icon--upload"><upload-filled /></el-icon>
            <div class="el-upload__text">
              将文件拖到此处，或<em>点击上传</em>
            </div>
            <template #tip>
              <div class="el-upload__tip">
                支持原图格式：JPG、PNG、GIF、WEBP，最大 50MB
              </div>
            </template>
          </el-upload>
        </el-form-item>
        <el-form-item label="图像生成模型">
          <el-select v-model="uploadForm.image_generation_model" style="width: 100%;">
            <el-option label="epd_packbits_rle_v1（生成设备流）" value="epd_packbits_rle_v1" />
            <el-option label="source_only_v1（仅保留原图）" value="source_only_v1" />
          </el-select>
        </el-form-item>
        <template v-if="uploadForm.image_generation_model === 'epd_packbits_rle_v1'">
          <el-form-item label="模式">
            <el-radio-group v-model="uploadForm.epd_mode" size="small" @change="reprocessEpdPreview">
              <el-radio-button
                v-for="m in epdModeOptions"
                :key="m.value"
                :label="m.value"
              >
                {{ m.label }}
              </el-radio-button>
            </el-radio-group>
          </el-form-item>
          <el-form-item label="设备流预览">
            <div class="epd-preview-row">
              <canvas
                ref="epdPreviewCanvasRef"
                class="epd-preview-canvas"
                width="200"
                height="200"
              />
              <div class="epd-preview-meta">
                <template v-if="epdPreviewRleLen != null">
                  <span>RLE {{ epdPreviewRleLen }} 字节</span>
                </template>
                <span v-else class="epd-preview-hint">选择原图后显示预览</span>
              </div>
            </div>
          </el-form-item>
          <template v-if="uploadForm.epd_mode !== 'bw'">
            <el-form-item label="饱和度">
              <div style="width: 100%; display: flex; align-items: center; gap: 12px;">
                <el-slider
                  v-model="uploadForm.epd_saturate"
                  :min="0.5"
                  :max="2.5"
                  :step="0.05"
                  style="flex: 1;"
                  @change="reprocessEpdPreview"
                />
                <span style="min-width: 44px; text-align: right;">{{ uploadForm.epd_saturate.toFixed(2) }}</span>
              </div>
            </el-form-item>
            <el-form-item label="对比度">
              <div style="width: 100%; display: flex; align-items: center; gap: 12px;">
                <el-slider
                  v-model="uploadForm.epd_contrast"
                  :min="0.5"
                  :max="2.5"
                  :step="0.05"
                  style="flex: 1;"
                  @change="reprocessEpdPreview"
                />
                <span style="min-width: 44px; text-align: right;">{{ uploadForm.epd_contrast.toFixed(2) }}</span>
              </div>
            </el-form-item>
            <el-form-item label="色相">
              <div style="width: 100%; display: flex; align-items: center; gap: 12px;">
                <el-slider
                  v-model="uploadForm.epd_hue"
                  :min="-180"
                  :max="180"
                  :step="1"
                  style="flex: 1;"
                  @change="reprocessEpdPreview"
                />
                <span style="min-width: 44px; text-align: right;">{{ uploadForm.epd_hue }}°</span>
              </div>
            </el-form-item>
          </template>
          <el-form-item label="锐化">
            <div style="width: 100%; display: flex; align-items: center; gap: 12px;">
              <el-slider
                v-model="uploadForm.epd_sharpen"
                :min="0"
                :max="2"
                :step="0.1"
                style="flex: 1;"
                @change="reprocessEpdPreview"
              />
              <span style="min-width: 44px; text-align: right;">{{ uploadForm.epd_sharpen.toFixed(1) }}</span>
            </div>
            <div style="font-size: 12px; color: #909399; margin-top: 4px;">
              服务端在量化前应用；下方预览为 4ct 算法预览，不含锐化。
            </div>
          </el-form-item>
        </template>
      </el-form>
      
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="showUploadDialog = false">取消</el-button>
          <el-button
            type="primary"
            :loading="uploading"
            @click="handleUpload"
          >
            上传
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 详情对话框 -->
    <el-dialog
      v-model="showDetailDialog"
      title="表情包详情"
      width="800px"
    >
      <div v-if="currentEmotion" class="emotion-detail">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="名称">{{ currentEmotion.name }}</el-descriptions-item>
          <el-descriptions-item label="版本">{{ currentEmotion.version }}</el-descriptions-item>
          <el-descriptions-item label="表情包ID" :span="2">
            <div style="display:flex;align-items:center;gap:8px;">
              <code style="font-size:12px;">{{ currentEmotion.id }}</code>
              <el-button size="small" text type="primary" @click="copyToClipboard(currentEmotion.id)">复制</el-button>
            </div>
          </el-descriptions-item>
          <el-descriptions-item label="分类">{{ currentEmotion.category || '未分类' }}</el-descriptions-item>
          <el-descriptions-item label="内容说明" :span="2">{{ currentEmotion.content || '无' }}</el-descriptions-item>
          <el-descriptions-item label="火热程度">
            <el-rate :model-value="currentEmotion.hot || 5" disabled :max="5" />
          </el-descriptions-item>
          <el-descriptions-item label="文件状态">
            <el-tag :type="currentEmotion.file_exists ? 'success' : 'danger'">
              {{ currentEmotion.file_exists ? '存在' : '缺失' }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="创建时间">{{ formatDate(currentEmotion.created_at) }}</el-descriptions-item>
          <el-descriptions-item
            v-if="detailEpdParams"
            label="EPD 生成参数"
            :span="2"
          >
            模式 {{ detailEpdParams.mode || 'eink' }}、饱和 {{ detailEpdParams.saturate }}、对比 {{ detailEpdParams.contrast }}、色相 {{ detailEpdParams.hue }}、锐化 {{ detailEpdParams.sharpen }}
          </el-descriptions-item>
        </el-descriptions>
        
        <!-- 封面图 -->
        <div v-if="currentEmotion.cover_image" style="margin-top: 20px;">
          <h4>封面图</h4>
          <div style="margin-top: 10px;">
            <img 
              :src="getImageUrl(currentEmotion.cover_image)" 
              alt="封面图"
              style="max-width: 300px; max-height: 300px; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);"
              @error="handleImageError"
            />
          </div>
        </div>
        
        <!-- 预览图 -->
        <div v-if="currentEmotion.preview_images && currentEmotion.preview_images.length > 0" style="margin-top: 20px;">
          <h4>预览图 ({{ currentEmotion.preview_images.length }} 张)</h4>
          <div style="margin-top: 10px; display: flex; flex-wrap: wrap; gap: 10px;">
            <img 
              v-for="(imgUrl, index) in currentEmotion.preview_images" 
              :key="index"
              :src="getImageUrl(imgUrl)" 
              :alt="`预览图 ${index + 1}`"
              style="max-width: 200px; max-height: 200px; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); cursor: pointer;"
              @error="handleImageError"
              @click="openImagePreview(imgUrl)"
            />
          </div>
        </div>
        
        <div v-if="currentEmotion.zip_error" class="error-info">
          <el-alert
            :title="`ZIP文件解析错误: ${currentEmotion.zip_error}`"
            type="error"
            show-icon
          />
        </div>
      </div>
    </el-dialog>

    <el-dialog
      v-model="showImagePreviewDialog"
      title="图片预览"
      width="720px"
      append-to-body
    >
      <div style="display:flex;justify-content:center;align-items:center;min-height:220px;">
        <img
          v-if="previewImageUrl"
          :src="previewImageUrl"
          alt="图片预览"
          style="max-width:100%;max-height:70vh;object-fit:contain;"
          @error="handleImageError"
        />
      </div>
    </el-dialog>

    <!-- 编辑对话框 -->
    <el-dialog
      v-model="showEditDialog"
      title="编辑表情包"
      :width="dialogWidth"
      :close-on-click-modal="false"
    >
      <el-form
        ref="editFormRef"
        :model="editForm"
        :rules="editRules"
        label-width="100px"
      >
        <el-form-item label="表情包名称" prop="name">
          <el-input v-model="editForm.name" placeholder="请输入表情包名称" />
        </el-form-item>
        <el-form-item label="版本号" prop="version">
          <el-input v-model="editForm.version" placeholder="如：1.0.0" />
        </el-form-item>
        <el-form-item label="分类" prop="category">
          <el-select
            v-model="editForm.category"
            style="width: 100%;"
            placeholder="请选择或输入分类名称"
            filterable
            allow-create
            default-first-option
          >
            <el-option v-for="item in categoryOptions" :key="item" :label="item" :value="item" />
          </el-select>
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input
            v-model="editForm.description"
            type="textarea"
            :rows="3"
            placeholder="请输入表情包描述"
          />
        </el-form-item>
        <el-form-item label="内容说明" prop="content">
          <el-input
            v-model="editForm.content"
            type="textarea"
            :rows="3"
            placeholder="请输入表情包内容说明"
          />
        </el-form-item>
        <el-form-item v-if="isAdmin" label="火热程度" prop="hot">
          <el-input-number
            v-model="editForm.hot"
            :min="1"
            :max="5"
            :step="1"
            placeholder="1-5，5最火爆"
            style="width: 100%;"
          />
          <div style="font-size: 12px; color: #909399; margin-top: 4px;">
            1表示最不火爆，5表示最火爆
          </div>
        </el-form-item>
        <el-form-item label="适配版型" prop="board_types">
          <el-checkbox-group v-model="editForm.board_types">
            <el-checkbox 
              v-for="board in boardTypes" 
              :key="board.id" 
              :value="board.id"
            >
              {{ board.name }}
            </el-checkbox>
          </el-checkbox-group>
        </el-form-item>
        <el-form-item label="封面图">
            <el-upload
              :auto-upload="false"
              :on-change="handleEditCoverImageChange"
              :before-upload="beforeImageUpload"
              :file-list="editForm.coverImageList"
              :limit="1"
              accept="image/*"
              list-type="picture-card"
              :multiple="false"
            :on-exceed="handleCoverImageExceed"
            >
              <el-icon><Plus /></el-icon>
            </el-upload>
          <div style="font-size: 12px; color: #909399; margin-top: 4px;">
            支持格式：JPG, PNG, GIF, WebP, SVG 等，最大 5MB，只能上传一张
          </div>
        </el-form-item>
        <el-form-item label="预览图（最多4个）">
          <div style="display: flex; gap: 12px; align-items: flex-start; flex-wrap: wrap;">
            <el-upload
              :auto-upload="false"
              :on-change="handleEditPreviewImagesChange"
              :on-remove="handleEditPreviewImageRemove"
              :before-upload="beforeImageUpload"
              :file-list="editForm.previewImageList"
              :limit="4"
              accept="image/*"
              list-type="picture-card"
              multiple
            >
              <el-icon><Plus /></el-icon>
            </el-upload>
            <div v-if="editForm.preview_images" style="flex: 1; min-width: 300px;">
              <el-input
                v-model="editForm.preview_images"
                type="textarea"
                :rows="3"
                placeholder="或直接输入预览图URL，多个URL用逗号分隔"
              />
              <div style="font-size: 12px; color: #909399; margin-top: 4px;">
                多个URL请用逗号分隔
              </div>
            </div>
          </div>
          <div style="font-size: 12px; color: #909399; margin-top: 4px;">
            支持格式：JPG, PNG, GIF, WebP, SVG 等，每个最大 5MB，最多上传4个
          </div>
        </el-form-item>
        <el-form-item v-if="isAdmin" label="运营购买数">
          <el-input-number
            v-model="editForm.display_purchase_count"
            :min="0"
            placeholder="运营可编辑的已购买数（用于营销展示）"
            style="width: 100%;"
          />
          <div style="font-size: 12px; color: #909399; margin-top: 4px;">
            用于营销展示的购买数，可与真实购买数叠加显示
          </div>
        </el-form-item>
        <el-form-item label="ZIP文件（可选）">
          <div v-if="currentEmotion?.file_path" style="margin-bottom: 12px; padding: 12px; background-color: #f5f7fa; border-radius: 4px;">
            <div style="display: flex; align-items: center; gap: 8px;">
              <el-icon><Document /></el-icon>
              <span style="font-weight: 500;">当前文件：</span>
              <span>{{ getFileNameFromPath(currentEmotion.file_path) }}</span>
              <el-tag :type="currentEmotion.file_exists ? 'success' : 'danger'" size="small">
                {{ currentEmotion.file_exists ? '存在' : '缺失' }}
              </el-tag>
            </div>
            <div v-if="currentEmotion.file_size" style="margin-top: 4px; font-size: 12px; color: #909399;">
              文件大小：{{ formatFileSize(currentEmotion.file_size) }}
            </div>
          </div>
          <el-upload
            ref="editUploadRef"
            :auto-upload="false"
            :on-change="handleEditFileChange"
            :before-upload="beforeUpload"
            :file-list="editForm.fileList"
            accept=".zip,.bin"
            drag
          >
            <el-icon class="el-icon--upload"><upload-filled /></el-icon>
            <div class="el-upload__text">
              将ZIP/BIN文件拖到此处，或<em>点击上传</em>
            </div>
            <template #tip>
              <div class="el-upload__tip">
                支持ZIP或BIN格式的表情包文件，上传新文件将替换现有文件
              </div>
            </template>
          </el-upload>
        </el-form-item>
      </el-form>
      
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="showEditDialog = false">取消</el-button>
          <el-button
            type="primary"
            :loading="editing"
            @click="handleEdit"
          >
            保存
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 授权对话框 -->
    <el-dialog
      v-model="showGrantDialog"
      title="授权表情包给用户账号"
      width="600px"
    >
      <div v-if="currentGrantEmotion">
        <p><strong>表情包：</strong>{{ currentGrantEmotion.name }} ({{ currentGrantEmotion.version }})</p>
        <el-divider />
        
        <div style="margin-bottom: 20px;">
          <h4>授权给用户</h4>
          <el-select
            v-model="grantForm.user_id"
            placeholder="输入用户名、昵称或 ID 搜索用户"
            style="width: 100%;"
            filterable
            clearable
            remote
            :remote-method="remoteSearchGrantUsers"
            :loading="grantUserSearchLoading"
          >
            <el-option
              v-for="user in availableUsers"
              :key="user.id"
              :label="getGrantUserDisplayName(user)"
              :value="user.id"
            />
          </el-select>
          <el-button
            type="primary"
            style="margin-top: 10px;"
            @click="handleGrantAccess"
            :disabled="!grantForm.user_id"
          >
            授权
          </el-button>
        </div>

        <el-divider />

        <div>
          <h4>已授权用户</h4>
          <el-table :data="grantedUsers" size="small" style="margin-top: 10px;">
            <el-table-column prop="username" label="用户名">
              <template #default="{ row }">
                <span>{{ row.username }}</span>
                <el-tag v-if="row.is_owner" type="info" size="small" style="margin-left: 8px;">自动可用</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="assigned_at" label="授权时间" width="180">
              <template #default="{ row }">
                <span v-if="row.assigned_at">{{ formatDate(row.assigned_at) }}</span>
                <span v-else style="color: #909399;">自动可用</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="120">
              <template #default="{ row }">
                <el-button
                  v-if="!row.is_owner"
                  type="danger"
                  size="small"
                  @click="onRevokeAccess(row)"
                >
                  取消授权
                </el-button>
                <span v-else style="color: #909399; font-size: 12px;">无需操作</span>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="showGrantDialog = false">关闭</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed, shallowRef, watch, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Upload, Refresh, UploadFilled, Plus, Document } from '@element-plus/icons-vue';
import { 
  getEmotionPackages, 
  getPackCategories,
  getEmotionPackage, 
  uploadEmotionPackage, 
  deleteEmotionPackage, 
  downloadEmotionPackage,
  updateEmotionPackage,
  uploadEmotionImage,
  approveEmotionPackage,
  publishEmotionPackage,
  grantEmotionToAccount,
  revokeEmotionFromAccount,
  getGrantedUsers
} from '../api/emotion';
import { getUsers } from '../api/auth';
import { getBoardTypes } from '../api/device';
import { processImageData, rleToRgba, EPD_WIDTH, EPD_HEIGHT } from '../lib/epdPackbits';

export default {
  name: 'EmotionManagement',
  components: {
    Upload,
    Refresh,
    UploadFilled,
    Plus,
    Document
  },
  setup() {
    const router = useRouter();
    const loading = ref(false);
    const uploading = ref(false);
    const editing = ref(false);
    const emotionPackages = ref([]);
    const showUploadDialog = ref(false);
    const showDetailDialog = ref(false);
    const showEditDialog = ref(false);
    const showGrantDialog = ref(false);
    const showImagePreviewDialog = ref(false);
    const previewImageUrl = ref('');
    const currentGrantEmotion = ref(null);
    const grantForm = reactive({
      user_id: null
    });
    const availableUsers = ref([]);
    const grantUserSearchLoading = ref(false);
    const grantedUsers = ref([]);
    const currentEmotion = ref(null);
    /** 详情页：GET /api/packs/{id} 从 notes 解析出的 epd_params_effective */
    const detailEpdParams = computed(() => {
      const epd = currentEmotion.value?.epd_params_effective;
      if (epd && typeof epd === 'object') return epd;
      return null;
    });
    const fileList = ref([]);
    const uploadRef = ref();
    const uploadFormRef = ref();
    const editFormRef = ref();
    const boardTypes = ref([]);
    const epdPreviewCanvasRef = ref(null);
    const epdRawImageData = shallowRef(null);
    const epdPreviewRleLen = ref(null);
    const epdModeOptions = [
      { value: 'rgb', label: 'Eink' },
      { value: 'hsv', label: 'HSV' },
      { value: 'eink', label: 'RGB' },
      { value: 'bw', label: 'B/W' },
    ];
    
    // 用户信息
    const currentUser = ref({ role: 'user' });
    const loadCurrentUser = () => {
      try {
        let user = {};
        const userStr = localStorage.getItem('user');
        if (userStr && userStr !== 'undefined') {
          user = JSON.parse(userStr);
        }
        currentUser.value = {
          username: user.username,
          role: user.role,
          id: user.id || null
        };
      } catch (e) {
        currentUser.value = { role: 'user' };
      }
    };
    const isAdmin = computed(() => currentUser.value.role === 'admin');

    /** 分类名称清洗：与后端 strip + 长度上限语义对齐（去首尾空白、零宽字符、全角空格、折叠空白） */
    const normalizeCategoryInput = (raw) => {
      if (raw == null) return '';
      return String(raw)
        .replace(/[\uFEFF\u200B-\u200D\u2060]/g, '')
        .replace(/\u3000/g, ' ')
        .trim()
        .replace(/\s+/g, ' ');
    };

    // 响应式对话框宽度
    const dialogWidth = computed(() => {
      if (typeof window !== 'undefined') {
        const width = window.innerWidth;
        if (width < 768) {
          return '95%';  // 移动端
        } else if (width < 1024) {
          return '80%';  // 平板
        } else {
          return '800px';  // 桌面端
        }
      }
      return '800px';
    });
    
    const uploadForm = reactive({
      name: '',
      version: '1.0.0',
      description: '',
      content: '',
      category: '日常',
      hot: null,  // 普通用户默认为空，管理员默认为5
      file: null,
      board_types: [], // 默认为空数组
      cover_image: '',
      coverImageList: [],
      preview_images: '',
      previewImageList: [],
      image_generation_model: 'epd_packbits_rle_v1',
      epd_mode: 'rgb',
      epd_saturate: 1.4,
      epd_contrast: 1.4,
      epd_hue: 0,
      epd_sharpen: 0
    });

    const editForm = reactive({
      name: '',
      version: '',
      description: '',
      content: '',
      category: '日常',
      hot: 5,
      board_types: [],
      cover_image: '',
      coverImageList: [],
      preview_images: '',
      previewImageList: [],
      display_purchase_count: 0,
      fileList: []
    });
    const editUploadRef = ref();
    /** 下拉默认靠前；其余来自库中已用过的 category */
    const CATEGORY_DEFAULT_PRESETS = ['日常', '限时'];
    const categoryOptionsFromApi = ref([]);

    const sortCategoryDropdown = (names) => {
      const list = (Array.isArray(names) ? names : [])
        .map((x) => String(x || '').trim())
        .filter(Boolean);
      const uniq = [...new Set(list)];
      const pin = CATEGORY_DEFAULT_PRESETS.filter((p) => uniq.includes(p));
      const tail = uniq
        .filter((n) => !pin.includes(n))
        .sort((a, b) => a.localeCompare(b, 'zh-Hans-CN'));
      return [...pin, ...tail];
    };

    const categoryOptions = computed(() =>
      sortCategoryDropdown([...CATEGORY_DEFAULT_PRESETS, ...categoryOptionsFromApi.value]),
    );

    const loadPackCategories = async () => {
      try {
        const res = await getPackCategories();
        const raw =
          res && res.success !== false && res.data && Array.isArray(res.data.list)
            ? res.data.list
            : [];
        categoryOptionsFromApi.value = raw.map((x) => String(x || '').trim()).filter(Boolean);
      } catch (e) {
        console.error('加载表情分类列表失败', e);
        categoryOptionsFromApi.value = [];
      }
    };

    const goBuildPack = () => {
      router.push('/packs/build');
    };

    const uploadRules = {
      name: [
        { required: true, message: '请输入表情包名称', trigger: 'blur' }
      ],
      version: [
        { required: true, message: '请输入版本号', trigger: 'blur' }
      ],
      category: [
        { required: true, message: '请选择或输入分类', trigger: ['change', 'blur'] },
        { type: 'string', max: 64, message: '分类名称不能超过 64 个字符', trigger: ['blur', 'change'] }
      ],
      file: [
        { required: true, message: '请选择原图文件', trigger: 'change' }
      ]
    };

    const editRules = {
      name: [
        { required: true, message: '请输入表情包名称', trigger: 'blur' }
      ],
      version: [
        { required: true, message: '请输入版本号', trigger: 'blur' }
      ],
      category: [
        { required: true, message: '请选择或输入分类', trigger: ['change', 'blur'] },
        { type: 'string', max: 64, message: '分类名称不能超过 64 个字符', trigger: ['blur', 'change'] }
      ]
    };

    // 获取表情包列表
    const fetchEmotionPackages = async () => {
      try {
        loading.value = true;
        const response = await getEmotionPackages();
        if (response.success) {
          const rows = Array.isArray(response.data) ? [...response.data] : [];
          // Force UI ordering by created time descending.
          rows.sort((a, b) => {
            const ta = new Date(a?.created_at || 0).getTime();
            const tb = new Date(b?.created_at || 0).getTime();
            return tb - ta;
          });
          emotionPackages.value = rows;
        } else {
          ElMessage.error(response.message || '获取表情包列表失败');
        }
      } catch (error) {
        ElMessage.error('获取表情包列表失败');
        console.error(error);
      } finally {
        loading.value = false;
      }
    };

    // 刷新表情包列表
    const refreshEmotionList = () => {
      fetchEmotionPackages();
      loadPackCategories();
    };

    // 文件上传前检查
    const beforeUpload = (file) => {
      const lowerName = (file.name || '').toLowerCase();
      const isValidType = file.type.startsWith('image/') ||
        lowerName.endsWith('.png') ||
        lowerName.endsWith('.jpg') ||
        lowerName.endsWith('.jpeg') ||
        lowerName.endsWith('.gif') ||
        lowerName.endsWith('.webp');
      const isLt50M = file.size / 1024 / 1024 < 50;

      if (!isValidType) {
        ElMessage.error('只能上传图片原图文件（JPG/PNG/GIF/WEBP）!');
        return false;
      }
      if (!isLt50M) {
        ElMessage.error('文件大小不能超过 50MB!');
        return false;
      }
      return true;
    };

    const clearEpdPreviewState = () => {
      epdRawImageData.value = null;
      epdPreviewRleLen.value = null;
      nextTick(() => {
        const canvas = epdPreviewCanvasRef.value;
        if (canvas) {
          const ctx = canvas.getContext('2d');
          if (ctx) ctx.clearRect(0, 0, EPD_WIDTH, EPD_HEIGHT);
        }
      });
    };

    const reprocessEpdPreview = () => {
      nextTick(() => {
        const canvas = epdPreviewCanvasRef.value;
        const raw = epdRawImageData.value;
        if (!canvas || !raw) {
          if (canvas) {
            const ctx = canvas.getContext('2d');
            if (ctx) ctx.clearRect(0, 0, EPD_WIDTH, EPD_HEIGHT);
          }
          epdPreviewRleLen.value = null;
          return;
        }
        const rle = processImageData(
          raw,
          uploadForm.epd_mode,
          uploadForm.epd_saturate,
          uploadForm.epd_contrast,
          uploadForm.epd_hue,
        );
        epdPreviewRleLen.value = rle.length;
        const rgba = rleToRgba(rle);
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const imgData = ctx.createImageData(EPD_WIDTH, EPD_HEIGHT);
        imgData.data.set(rgba);
        ctx.putImageData(imgData, 0, 0);
      });
    };

    const loadEpdFromFile = (file) => {
      if (!file || uploadForm.image_generation_model !== 'epd_packbits_rle_v1') {
        clearEpdPreviewState();
        return;
      }
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = EPD_WIDTH;
          canvas.height = EPD_HEIGHT;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            clearEpdPreviewState();
            return;
          }
          ctx.drawImage(img, 0, 0, EPD_WIDTH, EPD_HEIGHT);
          epdRawImageData.value = ctx.getImageData(0, 0, EPD_WIDTH, EPD_HEIGHT);
          reprocessEpdPreview();
        } finally {
          URL.revokeObjectURL(url);
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        clearEpdPreviewState();
        ElMessage.error('原图无法解码为 EPD 预览');
      };
      img.src = url;
    };

    watch(
      () => uploadForm.image_generation_model,
      (m) => {
        if (m === 'epd_packbits_rle_v1' && uploadForm.file) {
          loadEpdFromFile(uploadForm.file);
        } else if (m !== 'epd_packbits_rle_v1') {
          clearEpdPreviewState();
        }
      },
    );

    // 处理文件选择
    const handleFileChange = (file, files) => {
      fileList.value = files;
      uploadForm.file = file.raw;
      if (uploadForm.image_generation_model === 'epd_packbits_rle_v1' && file.raw) {
        loadEpdFromFile(file.raw);
      } else {
        clearEpdPreviewState();
      }
    };

    // 处理文件上传
    const handleUpload = async () => {
      try {
        uploadForm.category = normalizeCategoryInput(uploadForm.category);
        await uploadFormRef.value.validate();
        
        if (!uploadForm.file) {
          ElMessage.warning('请选择要上传的原图文件');
          return;
        }
        
        uploading.value = true;
        const response = await uploadEmotionPackage(
          uploadForm.file,
          uploadForm.name,
          uploadForm.version,
          uploadForm.description,
          uploadForm.content,
          uploadForm.category,
          uploadForm.hot,
          null,
          null,
          uploadForm.board_types,
          uploadForm.image_generation_model,
          uploadForm.image_generation_model === 'epd_packbits_rle_v1'
            ? {
                mode: uploadForm.epd_mode,
                saturate: uploadForm.epd_saturate,
                contrast: uploadForm.epd_contrast,
                hue: uploadForm.epd_hue,
                sharpen: uploadForm.epd_sharpen
              }
            : null
        );
        
        if (response.success) {
          ElMessage.success('表情包上传成功');
          showUploadDialog.value = false;
          resetUploadForm();
          fetchEmotionPackages();
          loadPackCategories();
        } else {
          ElMessage.error(response.message || '表情包上传失败');
        }
      } catch (error) {
        console.error('表情包上传异常:', error);
        const errorMessage = error.response?.data?.message || error.message || '表情包上传失败';
        ElMessage.error(errorMessage);
      } finally {
        uploading.value = false;
      }
    };

    // 图片上传前检查
    const beforeImageUpload = (file) => {
      const isValidType = file.type.startsWith('image/');
      const isLt5M = file.size / 1024 / 1024 < 5;

      if (!isValidType) {
        ElMessage.error('只能上传图片文件!');
        return false;
      }
      if (!isLt5M) {
        ElMessage.error('图片大小不能超过 5MB!');
        return false;
      }
      return true;
    };

    // 处理封面图超出限制（上传表单）
    const handleCoverImageExceed = () => {
      ElMessage.warning('封面图只能上传一张，请先删除现有图片');
    };

    // 处理封面图上传（上传表单）
    const handleCoverImageChange = async (file, fileList) => {
      // 封面图只允许一张，强制限制为1个文件
      if (fileList.length > 1) {
        // 保留最新的文件，移除旧的
        uploadForm.coverImageList = [fileList[fileList.length - 1]];
        ElMessage.warning('封面图只能上传一张，已自动替换为最新选择的图片');
        return; // 直接返回，不继续处理
      }
      
      // 确保只保留一个文件
      uploadForm.coverImageList = fileList.length > 0 ? [fileList[fileList.length - 1]] : [];
      
      // 只在文件状态为 ready 时上传，避免重复上传
      if (file.raw && file.status === 'ready' && uploadForm.coverImageList.length === 1) {
        try {
          const response = await uploadEmotionImage(file.raw);
          if (response && response.success && response.data) {
            // 保存相对路径URL（后端返回的是相对路径）
            const imageUrl = response.data.url;
            uploadForm.cover_image = imageUrl;
            // 更新文件列表的URL和状态（用于显示预览）
            if (uploadForm.coverImageList.length > 0) {
              uploadForm.coverImageList[0].url = getImageUrl(imageUrl);
              uploadForm.coverImageList[0].status = 'success';
              uploadForm.coverImageList[0].name = file.name || 'cover.jpg';
            }
            ElMessage.success('封面图上传成功');
          } else {
            const errorMsg = (response && response.message) || '封面图上传失败';
            ElMessage.error(errorMsg);
            console.error('封面图上传失败:', response);
            uploadForm.coverImageList = uploadForm.coverImageList.filter(f => f.uid !== file.uid);
          }
        } catch (error) {
          console.error('封面图上传异常:', error);
          ElMessage.error('封面图上传失败: ' + (error.message || error));
          uploadForm.coverImageList = uploadForm.coverImageList.filter(f => f.uid !== file.uid);
        }
      }
    };

    // 处理预览图上传（上传表单）
    const handlePreviewImagesChange = async (file, fileList) => {
      if (fileList.length > 4) {
        ElMessage.warning('预览图最多只能上传4个');
        uploadForm.previewImageList = fileList.slice(0, 4);
        return;
      }
      uploadForm.previewImageList = fileList;
      if (file.raw && file.status === 'ready') {
        try {
          const response = await uploadEmotionImage(file.raw);
          if (response.success && response.data) {
            // 保存图片URL（可能是CDN完整URL或相对路径）
            const imageUrl = response.data.url;
            // 添加到预览图列表
            const currentUrls = uploadForm.preview_images ? uploadForm.preview_images.split(',').filter(u => u) : [];
            currentUrls.push(imageUrl);
            uploadForm.preview_images = currentUrls.join(',');
            // 更新文件列表的URL（用于显示）
            const fileIndex = uploadForm.previewImageList.findIndex(f => f.uid === file.uid);
            if (fileIndex >= 0) {
              uploadForm.previewImageList[fileIndex].url = getImageUrl(imageUrl);
            }
            ElMessage.success('预览图上传成功');
          } else {
            ElMessage.error(response.message || '预览图上传失败');
            uploadForm.previewImageList = uploadForm.previewImageList.filter(f => f.uid !== file.uid);
          }
        } catch (error) {
          ElMessage.error('预览图上传失败');
          uploadForm.previewImageList = uploadForm.previewImageList.filter(f => f.uid !== file.uid);
        }
      }
    };

    // 处理封面图超出限制（编辑表单）
    const handleEditCoverImageExceed = () => {
      ElMessage.warning('封面图只能上传一张，请先删除现有图片');
    };

    // 处理封面图上传（编辑表单）
    const handleEditCoverImageChange = async (file, fileList) => {
      // 封面图只允许一张，强制限制为1个文件
      if (fileList.length > 1) {
        // 保留最新的文件，移除旧的
        editForm.coverImageList = [fileList[fileList.length - 1]];
        ElMessage.warning('封面图只能上传一张，已自动替换为最新选择的图片');
        return; // 直接返回，不继续处理
      }
      
      // 确保只保留一个文件
      editForm.coverImageList = fileList.slice(0, 1);
      
      // 只在文件状态为 ready 时上传，避免重复上传
      if (file.raw && file.status === 'ready' && editForm.coverImageList.length === 1) {
        try {
          const response = await uploadEmotionImage(file.raw);
          if (response && response.success && response.data) {
            // 保存相对路径URL（后端返回的是相对路径）
            editForm.cover_image = response.data.url;
            // 更新文件列表的URL（用于显示）
            if (editForm.coverImageList.length > 0) {
              editForm.coverImageList[0].url = getImageUrl(response.data.url);
            }
            ElMessage.success('封面图上传成功');
          } else {
            const errorMsg = (response && response.message) || '封面图上传失败';
            ElMessage.error(errorMsg);
            console.error('封面图上传失败:', response);
            editForm.coverImageList = editForm.coverImageList.filter(f => f.uid !== file.uid);
          }
        } catch (error) {
          console.error('封面图上传异常:', error);
          ElMessage.error('封面图上传失败: ' + (error.message || error));
          editForm.coverImageList = editForm.coverImageList.filter(f => f.uid !== file.uid);
        }
      }
    };

    // 处理预览图上传（编辑表单）
    const handleEditPreviewImagesChange = async (file, fileList) => {
      if (fileList.length > 4) {
        ElMessage.warning('预览图最多只能上传4个');
        editForm.previewImageList = fileList.slice(0, 4);
        return;
      }
      editForm.previewImageList = fileList;
      if (file.raw && file.status === 'ready') {
        try {
          const response = await uploadEmotionImage(file.raw);
          if (response.success && response.data) {
            // 保存图片URL（可能是CDN完整URL或相对路径）
            const imageUrl = response.data.url;
            const currentUrls = editForm.preview_images ? editForm.preview_images.split(',').filter(u => u) : [];
            currentUrls.push(imageUrl);
            editForm.preview_images = currentUrls.join(',');
            // 更新文件列表的URL（用于显示）
            const fileIndex = editForm.previewImageList.findIndex(f => f.uid === file.uid);
            if (fileIndex >= 0) {
              editForm.previewImageList[fileIndex].url = getImageUrl(imageUrl);
            }
            ElMessage.success('预览图上传成功');
          } else {
            ElMessage.error(response.message || '预览图上传失败');
            editForm.previewImageList = editForm.previewImageList.filter(f => f.uid !== file.uid);
          }
        } catch (error) {
          ElMessage.error('预览图上传失败');
          editForm.previewImageList = editForm.previewImageList.filter(f => f.uid !== file.uid);
        }
      }
    };

    // 处理预览图删除（编辑表单）
    const handleEditPreviewImageRemove = (file, fileList) => {
      editForm.previewImageList = fileList;
      
      // 从文本字段中移除对应的URL
      if (file.url) {
        // 从完整URL中提取文件名（用于匹配）
        let filename = null;
        if (file.url.includes('/packs/images/')) {
          const match = file.url.match(/\/packs\/images\/([^?]+)/);
          if (match) {
            filename = match[1];
          }
        } else if (file.url.includes('/api/packs/images/')) {
          const match = file.url.match(/\/api\/packs\/images\/([^?]+)/);
          if (match) {
            filename = match[1];
          }
        }
        
        // 从 preview_images 文本字段中移除匹配的URL
        if (editForm.preview_images && filename) {
          const urls = editForm.preview_images.split(',').map(u => u.trim()).filter(u => u);
          // 移除包含相同文件名的URL
          const filteredUrls = urls.filter(url => {
            // 提取URL中的文件名
            const urlFilename = url.split('?')[0].split('/').pop();
            return urlFilename !== filename;
          });
          editForm.preview_images = filteredUrls.length > 0 ? filteredUrls.join(',') : '';
        }
      }
    };

    // 获取图片URL（处理相对路径）
    // 管理后台的图片添加 ?admin=true 参数，后端会通过Referer识别管理员
    const getImageUrl = (imageUrl) => {
      if (!imageUrl || !imageUrl.trim()) {
        return '';
      }
      
      // 如果已经是完整URL（http/https开头），Referer会自动识别，不需要admin参数
      if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
        return imageUrl;
      }
      
      // 如果是相对路径的API图片，需要添加baseUrl和admin参数
      if (imageUrl.startsWith('/api/')) {
        const baseUrl = process.env.VUE_APP_API_BASE_URL || '';
        let fullUrl = imageUrl;
        
        if (baseUrl) {
          // 如果baseUrl已经包含/api，直接拼接；否则需要调整
          if (baseUrl.endsWith('/api')) {
            fullUrl = `${baseUrl}${imageUrl.replace('/api', '')}`;
          } else {
            fullUrl = `${baseUrl}${imageUrl}`;
          }
        } else {
          // 如果没有baseUrl，使用相对路径（假设通过nginx代理）
          fullUrl = imageUrl;
        }
        
        // 管理后台通过Referer自动识别为管理员，不需要admin参数（不安全）
        // Referer会自动被后端识别（如果是从cms.aigcute.com访问）
        
        return fullUrl;
      }
      
      return imageUrl;
    };

    const openImagePreview = (imagePath) => {
      const imageUrl = getImageUrl(imagePath);
      if (!imageUrl) return;
      previewImageUrl.value = imageUrl;
      showImagePreviewDialog.value = true;
    };

    // 图片加载错误处理
    const handleImageError = (event) => {
      const imgSrc = event.target.src;
      console.error('图片加载失败:', imgSrc);
      
      // 如果是CDN URL加载失败，可能是CORS或网络问题
      // 不进行重试，直接隐藏图片或显示占位符
      if (imgSrc && (imgSrc.startsWith('http://') || imgSrc.startsWith('https://'))) {
        // CDN图片加载失败，可能是CORS问题
        // 检查是否是七牛云CDN
        if (imgSrc.includes('imggc.aigcute.com')) {
          console.warn('七牛云CDN图片加载失败，可能是CORS配置问题:', imgSrc);
        }
        // 隐藏图片，避免重复尝试
      event.target.style.display = 'none';
      } else {
        event.target.style.display = 'none';
      }
    };

    // 重置上传表单
    const resetUploadForm = () => {
      uploadForm.name = '';
      uploadForm.version = '1.0.0';
      uploadForm.description = '';
      uploadForm.content = '';
      uploadForm.category = '日常';
      uploadForm.hot = isAdmin.value ? 5 : null;  // 管理员默认为5，普通用户为空
      uploadForm.file = null;
      uploadForm.cover_image = '';
      uploadForm.coverImageList = [];
      uploadForm.preview_images = '';
      uploadForm.previewImageList = [];
      uploadForm.image_generation_model = 'epd_packbits_rle_v1';
      uploadForm.epd_mode = 'rgb';
      uploadForm.epd_saturate = 1.4;
      uploadForm.epd_contrast = 1.4;
      uploadForm.epd_hue = 0;
      uploadForm.epd_sharpen = 0;
      fileList.value = [];
      uploadRef.value?.clearFiles();
      clearEpdPreviewState();
    };

    // 查看表情包详情
    const viewEmotionDetail = async (emotionId) => {
      try {
        const response = await getEmotionPackage(emotionId);
        if (response.success) {
          currentEmotion.value = response.data;
          showDetailDialog.value = true;
        } else {
          ElMessage.error(response.message || '获取表情包详情失败');
        }
      } catch (error) {
        ElMessage.error('获取表情包详情失败');
        console.error(error);
      }
    };

    // 编辑表情包
    const editEmotion = async (emotion) => {
      try {
        // 重新获取完整的表情包详情，确保包含所有字段
        const response = await getEmotionPackage(emotion.id);
        if (response.success && response.data) {
          const emotionDetail = response.data;
          editForm.name = emotionDetail.name || '';
          editForm.version = emotionDetail.version || '';
          editForm.description = emotionDetail.description || '';
          editForm.content = emotionDetail.content || '';
          editForm.category = normalizeCategoryInput(emotionDetail.category || '');
          editForm.hot = emotionDetail.hot || 5;
          editForm.board_types = emotionDetail.board_types ? emotionDetail.board_types.split(',') : [];
          editForm.cover_image = emotionDetail.cover_image || '';
          editForm.preview_images = Array.isArray(emotionDetail.preview_images) 
            ? emotionDetail.preview_images.join(',') 
            : (emotionDetail.preview_images || '');
          editForm.display_purchase_count = emotionDetail.display_purchase_count || 0;
          
          // 处理封面图和预览图的文件列表（确保URL包含baseUrl）
          editForm.coverImageList = emotionDetail.cover_image 
            ? [{ name: 'cover.jpg', url: getImageUrl(emotionDetail.cover_image) }]
            : [];
          editForm.previewImageList = Array.isArray(emotionDetail.preview_images) && emotionDetail.preview_images.length > 0
            ? emotionDetail.preview_images.map((url, index) => ({ name: `preview_${index + 1}.jpg`, url: getImageUrl(url) }))
            : [];
          
          currentEmotion.value = emotionDetail;
          showEditDialog.value = true;
        } else {
          ElMessage.error('获取表情包详情失败');
        }
      } catch (error) {
        console.error('获取表情包详情失败:', error);
        ElMessage.error('获取表情包详情失败');
      }
    };

    // 处理编辑文件变化
    const handleEditFileChange = (file, fileList) => {
      editForm.fileList = fileList;
    };

    // 处理编辑
    const handleEdit = async () => {
      try {
        editForm.category = normalizeCategoryInput(editForm.category);
        await editFormRef.value.validate();
        
        // 在更新前过滤预览图，确保不包含封面图
        let filteredPreviewImages = editForm.preview_images;
        if (editForm.preview_images && editForm.cover_image) {
          const previewUrls = editForm.preview_images.split(',').filter(u => u.trim());
          const coverBase = editForm.cover_image.split('?')[0];
          filteredPreviewImages = previewUrls
            .filter(url => {
              const urlBase = url.split('?')[0];
              return url !== editForm.cover_image && urlBase !== coverBase;
            })
            .join(',');
        }
        
        editing.value = true;
        
        // 如果上传了ZIP文件，使用FormData
        if (editForm.fileList && editForm.fileList.length > 0) {
          const formData = new FormData();
          formData.append('file', editForm.fileList[0].raw);
          formData.append('name', editForm.name);
          formData.append('version', editForm.version);
          if (editForm.description) formData.append('description', editForm.description);
          if (editForm.content) formData.append('content', editForm.content);
          formData.append('category', editForm.category || '');
          if (editForm.hot) formData.append('hot', editForm.hot.toString());
          if (editForm.board_types) formData.append('board_types', editForm.board_types.join(','));
          if (editForm.cover_image) formData.append('cover_image', editForm.cover_image);
          if (filteredPreviewImages) formData.append('preview_images', filteredPreviewImages);
          if (editForm.display_purchase_count) formData.append('display_purchase_count', editForm.display_purchase_count.toString());
          
          // 使用PUT请求发送FormData
          const response = await updateEmotionPackage(currentEmotion.value.id, formData, true);
          
          if (response.success) {
            ElMessage.success('表情包信息更新成功');
            showEditDialog.value = false;
            fetchEmotionPackages();
            loadPackCategories();
            // 清空文件列表
            editForm.fileList = [];
            editUploadRef.value?.clearFiles();
          } else {
            ElMessage.error(response.message || '表情包信息更新失败');
          }
        } else {
          // 没有文件，使用JSON格式
        const response = await updateEmotionPackage(currentEmotion.value.id, {
          name: editForm.name,
          version: editForm.version,
          description: editForm.description || null,
          content: editForm.content || null,
          category: editForm.category || null,
          hot: editForm.hot || 5,
          board_types: editForm.board_types ? editForm.board_types.join(',') : null,
          cover_image: editForm.cover_image || null,
          preview_images: filteredPreviewImages || null,
          display_purchase_count: editForm.display_purchase_count || 0
        });
        
        if (response.success) {
          ElMessage.success('表情包信息更新成功');
          showEditDialog.value = false;
          fetchEmotionPackages();
          loadPackCategories();
        } else {
          ElMessage.error(response.message || '表情包信息更新失败');
          }
        }
      } catch (error) {
        ElMessage.error('表情包信息更新失败');
        console.error(error);
      } finally {
        editing.value = false;
      }
    };

    // 下载表情包
    const downloadEmotion = async (emotionId) => {
      try {
        // 先获取表情包信息以获取正确的文件名
        const emotionInfo = await getEmotionPackage(emotionId);
        const emotion = emotionInfo.data || emotionInfo;
        const filename = `${emotion.name || 'emotion'}_v${emotion.version || '1.0.0'}`;
        
        const blob = await downloadEmotionPackage(emotionId);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        ElMessage.error('表情包下载失败');
        console.error(error);
      }
    };

    // 删除表情包
    const deleteEmotion = async (emotionId) => {
      try {
        await ElMessageBox.confirm(
          '确定要删除此表情包吗？此操作不可恢复。',
          '确认删除',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        );

        const response = await deleteEmotionPackage(emotionId);
        if (response.success) {
          ElMessage.success('表情包删除成功');
          fetchEmotionPackages();
        } else {
          ElMessage.error(response.message || '表情包删除失败');
        }
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('表情包删除失败');
          console.error(error);
        }
      }
    };
    
    const approveEmotion = async (emotionId) => {
      try {
        await ElMessageBox.confirm(
          '确定要审核通过此表情包吗？审核通过后，该表情包将在市场页面显示。',
          '审核确认',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        );
        
        const response = await approveEmotionPackage(emotionId, true);
        if (response.success) {
          ElMessage.success('审核通过成功');
          fetchEmotionPackages();
        } else {
          ElMessage.error(response.message || '审核失败');
        }
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('审核失败');
          console.error(error);
        }
      }
    };

    // 上架/下架表情包
    const togglePublishEmotion = async (emotionId, isPublished) => {
      try {
        const action = isPublished ? '上架' : '下架';
        await ElMessageBox.confirm(
          `确定要${action}此表情包吗？${isPublished ? '上架后表情包将在市场显示。' : '下架后表情包将从市场移除。'}`,
          `确认${action}`,
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        );
        
        const response = await publishEmotionPackage(emotionId, isPublished);
        if (response.success) {
          ElMessage.success(`${action}成功`);
          fetchEmotionPackages();
        } else {
          ElMessage.error(response.message || `${action}失败`);
        }
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error(`${isPublished ? '上架' : '下架'}失败`);
          console.error(error);
        }
      }
    };

    // 授权相关方法
    const onGrantAccess = async (emotion) => {
      currentGrantEmotion.value = emotion;
      grantForm.user_id = null;
      await loadUsers();
      await loadGrantedUsers(emotion.id);
      showGrantDialog.value = true;
    };

    // 远程搜索用户（授权下拉用），输入时动态请求
    const remoteSearchGrantUsers = async (query) => {
      if (!isAdmin.value) {
        const userStr = localStorage.getItem('user');
        if (userStr && userStr !== 'undefined') {
          const user = JSON.parse(userStr);
          availableUsers.value = [{ id: user.id, username: user.username, nickname: '' }];
        }
        return;
      }
      grantUserSearchLoading.value = true;
      try {
        const response = await getUsers({
          query: (query || '').trim(),
          page: 1,
          page_size: 50
        });
        if (response.success) {
          availableUsers.value = (response.data || []).filter(u => u.role !== 'admin');
        } else {
          availableUsers.value = [];
        }
      } catch (error) {
        ElMessage.error('搜索用户失败');
        availableUsers.value = [];
        console.error(error);
      } finally {
        grantUserSearchLoading.value = false;
      }
    };

    const getGrantUserDisplayName = (user) => {
      if (user.nickname) {
        return `${user.nickname} (${user.username || ''} ID: ${user.id})`;
      }
      return `${user.username || '用户'} (ID: ${user.id})`;
    };

    const loadUsers = async () => {
      if (!isAdmin.value) return;
      // 管理员：打开弹窗时先拉一页，方便直接选；之后靠输入触发远程搜索
      await remoteSearchGrantUsers('');
    };

    const loadGrantedUsers = async (emotionId) => {
      try {
        const response = await getGrantedUsers(emotionId);
        if (response.success) {
          grantedUsers.value = response.data;
        }
      } catch (error) {
        ElMessage.error('加载已授权用户失败');
        console.error(error);
      }
    };

    const handleGrantAccess = async () => {
      if (!grantForm.user_id) {
        ElMessage.warning('请选择要授权的用户');
        return;
      }
      if (!currentGrantEmotion.value) {
        return;
      }
      try {
        const response = await grantEmotionToAccount(currentGrantEmotion.value.id, grantForm.user_id);
        if (response.success) {
          ElMessage.success('表情包授权成功');
          await loadGrantedUsers(currentGrantEmotion.value.id);
          grantForm.user_id = null;
        } else {
          ElMessage.error(response.message || '表情包授权失败');
        }
      } catch (error) {
        ElMessage.error('表情包授权失败');
        console.error(error);
      }
    };

    const onRevokeAccess = async (grantRecord) => {
      if (!currentGrantEmotion.value) {
        return;
      }
      try {
        await ElMessageBox.confirm(
          `确定要取消用户 "${grantRecord.username}" 的表情包授权吗？`,
          '确认取消授权',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        );
        const response = await revokeEmotionFromAccount(currentGrantEmotion.value.id, grantRecord.user_id);
        if (response.success) {
          ElMessage.success('表情包授权已取消');
          await loadGrantedUsers(currentGrantEmotion.value.id);
        } else {
          ElMessage.error(response.message || '取消授权失败');
        }
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('取消授权失败');
          console.error(error);
        }
      }
    };

    // 格式化日期
    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleString();
    };

    const getUploaderDisplay = (row) => {
      const createdBy = row?.created_by;
      if (createdBy != null && String(createdBy).trim() !== '') {
        return `用户 ${createdBy}`;
      }
      return '平台';
    };

    const copyToClipboard = async (text) => {
      const content = String(text || '');
      if (!content) {
        ElMessage.warning('无可复制内容');
        return;
      }
      try {
        // Prefer modern Clipboard API in secure contexts.
        if (navigator?.clipboard?.writeText && window.isSecureContext) {
          await navigator.clipboard.writeText(content);
          ElMessage.success('已复制到剪贴板');
          return;
        }
        // Fallback for non-secure contexts / limited browser permissions.
        const textarea = document.createElement('textarea');
        textarea.value = content;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.top = '-9999px';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        textarea.setSelectionRange(0, content.length);
        const copied = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (!copied) {
          throw new Error('execCommand copy failed');
        }
        ElMessage.success('已复制到剪贴板');
      } catch (e) {
        ElMessage.error('复制失败');
      }
    };

    const formatShortId = (id) => {
      const value = String(id || '');
      if (!value) return '-';
      if (value.length <= 18) return value;
      return `${value.slice(0, 8)}...${value.slice(-6)}`;
    };

    // 从文件路径中提取文件名
    const getFileNameFromPath = (filePath) => {
      if (!filePath) return '';
      // 处理Windows和Unix路径
      const parts = filePath.split(/[/\\]/);
      return parts[parts.length - 1] || filePath;
    };

    // 格式化文件大小
    const formatFileSize = (bytes) => {
      if (!bytes || bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const loadBoardTypes = async () => {
      try {
        const response = await getBoardTypes();
        if (response.success) {
          boardTypes.value = response.data;
        }
      } catch (error) {
        console.error('获取版型列表失败:', error);
      }
    };

    const getBoardTypeNames = (boardTypesStr) => {
      if (!boardTypesStr || !String(boardTypesStr).trim()) {
        return '全部';
      }
      const typeIds = String(boardTypesStr)
        .replace(/，/g, ',')
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean);
      if (!typeIds.length) return '全部';
      if (!boardTypes.value.length) return typeIds.join('，');
      const idToName = new Map(boardTypes.value.map((b) => [String(b.id), b.name]));
      return typeIds.map((id) => idToName.get(id) || id).join('，');
    };

    onMounted(() => {
      loadCurrentUser();
      fetchEmotionPackages();
      loadPackCategories();
      loadBoardTypes();
    });

    return {
      loading,
      uploading,
      editing,
      emotionPackages,
      showUploadDialog,
      showDetailDialog,
      showEditDialog,
      showImagePreviewDialog,
      previewImageUrl,
      currentEmotion,
      detailEpdParams,
      fileList,
      uploadRef,
      uploadFormRef,
      editFormRef,
      uploadForm,
      editForm,
      uploadRules,
      editRules,
      categoryOptions,
      refreshEmotionList,
      beforeUpload,
      handleFileChange,
      handleUpload,
      beforeImageUpload,
      handleCoverImageChange,
      handleCoverImageExceed,
      handlePreviewImagesChange,
      handleEditCoverImageChange,
      handleEditCoverImageExceed,
      handleEditPreviewImageRemove,
      handleEditPreviewImagesChange,
      handleEditFileChange,
      getImageUrl,
      handleImageError,
      openImagePreview,
      viewEmotionDetail,
      editEmotion,
      handleEdit,
      downloadEmotion,
      deleteEmotion,
      approveEmotion,
      togglePublishEmotion,
      formatDate,
      getUploaderDisplay,
      copyToClipboard,
      formatShortId,
      getFileNameFromPath,
      formatFileSize,
      isAdmin,
      dialogWidth,
      currentUser,
      goBuildPack,
      loadCurrentUser,
      // 授权相关
      showGrantDialog,
      currentGrantEmotion,
      grantForm,
      availableUsers,
      grantUserSearchLoading,
      remoteSearchGrantUsers,
      getGrantUserDisplayName,
      grantedUsers,
      onGrantAccess,
      loadUsers,
      loadGrantedUsers,
      handleGrantAccess,
      onRevokeAccess,
      boardTypes,
      getBoardTypeNames,
      epdModeOptions,
      epdPreviewCanvasRef,
      epdPreviewRleLen,
      reprocessEpdPreview
    };
  }
};
</script>

<style scoped>
.emotion-management {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h1 {
  margin: 0;
  color: #303133;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.emotion-list {
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  overflow-x: auto;
}

.table-scroll {
  width: 100%;
  overflow-x: auto;
}

.table-scroll code {
  word-break: break-all;
}

@media (max-width: 1200px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
}

.dialog-footer {
  text-align: right;
}

.emotion-detail {
  padding: 20px 0;
}

.file-list {
  margin-top: 20px;
}

.file-list h4 {
  margin-bottom: 10px;
  color: #303133;
}

.error-info {
  margin-top: 20px;
}

:deep(.el-upload-dragger) {
  width: 100%;
  height: 180px;
}

:deep(.el-upload__tip) {
  color: #909399;
  font-size: 12px;
  margin-top: 7px;
}

/* 操作按钮布局优化 */
.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}

.action-buttons .el-button {
  margin: 0;
  padding: 4px 10px;
}

.epd-preview-row {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
}

.epd-preview-canvas {
  width: 200px;
  height: 200px;
  image-rendering: pixelated;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #f5f7fa;
}

.epd-preview-meta {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  color: #606266;
}

.epd-preview-hint {
  color: #909399;
  font-size: 12px;
}
</style>

